/* ============================================================
   CANONICAL CHANNEL SCHEMA — the single source of truth.
   ------------------------------------------------------------
   ONE Zod schema, imported by EVERYONE that touches channel data:
     • the app (static fallback, loader, store)
     • the Netlify functions (cron enrichment, /api/channels, config)
     • localStorage (user-added custom channels)
     • the add-channel form

   The TypeScript types are DERIVED from the schema (z.infer), so the
   shape is defined exactly once. Every boundary validates with
   parseChannel/parseChannels, so malformed data can never cross into
   the app — that's what makes "blob channels ∪ localStorage channels"
   safe to treat as one uniform list (and "add channel" nearly free).
   ============================================================ */
import { z } from 'zod'

/** One program (video) within a channel — enriched by the cron. */
export const ProgramSchema = z.object({
  videoId: z.string(),
  title: z.string().default(''),
  position: z.number().int().nonnegative().default(0),
  embeddable: z.boolean().default(true),
  /** Video length in seconds (from YouTube contentDetails). 0 = unknown. Used
   *  by Live mode to compute where the infinite broadcast loop is "now". */
  durationSeconds: z.number().nonnegative().default(0),
  /** Sponsor-ish ranges (video-seconds) to CUT, from SponsorBlock at enrich time. The queue builder
   *  derives the kept content portions from these; empty = whole video. */
  skipSegments: z.array(z.object({ start: z.number().nonnegative(), end: z.number().nonnegative() })).default([]),
})
export type Program = z.infer<typeof ProgramSchema>

/** Where a channel's videos come from — distinguishes provenance too. */
export const ChannelOrigin = z.enum(['builtin', 'remote', 'user'])
export type ChannelOrigin = z.infer<typeof ChannelOrigin>

const BreakItemSchema = z.object({
  scene: z.string(),
  params: z.record(z.string(), z.unknown()).default({}),
  maxMs: z.number().positive().default(5000),
  weight: z.number().positive().default(1),
})
export type BreakItem = z.infer<typeof BreakItemSchema>

export const SelectMode = z.enum(['random', 'sequence', 'weighted'])
export type SelectMode = z.infer<typeof SelectMode>

const BreakSlotSchema = z.object({
  select: SelectMode.default('random'),
  items: z.array(BreakItemSchema).min(1),
})
export type BreakSlot = z.infer<typeof BreakSlotSchema>

const AdSlotSchema = BreakSlotSchema.extend({
  enabled: z.boolean().default(true),
  /** @deprecated count-based cadence — used only when `everySeconds` is unset. */
  every: z.number().int().positive().default(4),
  /** Seconds of CONTENT between mid-content ads (splits items to insert an ad). When set, replaces
   *  the count-based `every`. The interval jitters ±`jitterPct`, deterministically (seeded). */
  everySeconds: z.number().positive().optional(),
  jitterPct: z.number().min(0).max(0.9).default(0.15),
})
export type AdSlot = z.infer<typeof AdSlotSchema>

const BreaksSchema = z.object({
  main: BreakSlotSchema.optional(),
  program: BreakSlotSchema.optional(),
  ads: AdSlotSchema.optional(),
})
export type Breaks = z.infer<typeof BreaksSchema>

/**
 * The canonical channel. Authored fields are required-with-defaults so a
 * minimal `{ number, name }` still parses into a complete object; derived
 * fields (programs, origin) are filled by the cron / loader.
 */
export const ChannelSchema = z.object({
  number: z.number().int(),
  name: z.string().min(1),
  slug: z.string().default(''),

  // source: a YouTube playlist (raw id or any url), and/or whole YouTube CHANNELS whose uploads are
  // summed in, and/or explicit video ids. Kept as authored; the loader/cron derives the rest.
  playlist: z.string().nullable().default(null),
  playlistId: z.string().nullable().default(null),
  videos: z.array(z.string()).default([]),
  // raw channel refs (UC… id, @handle, or any channel url) — enrichment sums each channel's uploads.
  // Scraped INCREMENTALLY: each refresh pages the uploads feed only until it reaches a video already
  // on record, then appends the new ones — so steady-state cost is the delta, not the whole history.
  youtubeChannels: z.array(z.string()).default([]),
  excludeVideoIds: z.array(z.string()).default([]),

  // on-air branding
  genre: z.string().default(''),
  tagline: z.string().default(''),
  ident: z.string().default('#3b6fff'),
  ink: z.string().default('#ffffff'),
  logo: z.string().default(''),
  bumper: z.string().default('sweep'),
  breaks: BreaksSchema.optional(),
  schedule: z.string().default('24H BROADCAST'),
  noSignal: z.boolean().default(false),
  hidden: z.boolean().default(false),
  // 'video' (default) plays YouTube; 'component' renders a custom React component instead of a player
  // (an interactive channel — see src/components/channelComponents.ts). `component` is the registry key.
  kind: z.enum(['video', 'component']).default('video'),
  component: z.string().nullable().default(null),
  // extra search aliases for the guide's search box (genre synonyms; for secret channels, the
  // exact-match discovery terms — see src/tv/guideSearch.ts).
  keywords: z.array(z.string()).default([]),
  // an "ad channel": its CONTENT is these bump scenes played back-to-back as queue items (no YouTube
  // videos, no injected ad breaks — the ads ARE the show). See buildChannelQueue.
  adReel: z.array(z.string()).default([]),

  // derived (cron / loader)
  programs: z.array(ProgramSchema).optional(),
  origin: ChannelOrigin.default('remote'),
})
export type Channel = z.infer<typeof ChannelSchema>

/** What the cron writes to the data blob / serves from /api/channels. */
export const ChannelsPayloadSchema = z.object({
  channels: z.array(ChannelSchema),
  refreshedAt: z.string().nullable().default(null),
  stale: z.boolean().optional(),
  errors: z.array(z.object({ number: z.number(), error: z.string() })).optional(),
})
export type ChannelsPayload = z.infer<typeof ChannelsPayloadSchema>

/* ---------- playlist / video id parsing (shared everywhere) ---------- */

/** Pull a playlist id out of a raw id or a full youtube url. */
export function parsePlaylist(input: string | null | undefined): string | null {
  if (!input) return null
  if (/^PL|^UU|^LL|^FL|^OL/.test(input) && !/[?&/]/.test(input)) return input
  const m = String(input).match(/[?&]list=([^&]+)/)
  return m ? m[1] : input
}

/** Pull an 11-char video id out of a raw id or any youtube url form. */
export function parseVideo(input: string | null | undefined): string | null {
  if (!input) return null
  const s = String(input).trim()
  const m =
    s.match(/[?&]v=([\w-]{11})/) ||
    s.match(/youtu\.be\/([\w-]{11})/) ||
    s.match(/\/embed\/([\w-]{11})/) ||
    s.match(/\/shorts\/([\w-]{11})/)
  if (m) return m[1]
  if (/^[\w-]{11}$/.test(s)) return s
  return null
}

/** A resolved YouTube channel reference. Exactly one of id / handle / username is set. */
export type ChannelRef = { id?: string; handle?: string; username?: string }

/** Parse a YouTube channel ref (a `UC…` id, an `@handle`, or any channel url) into a normalized
 *  shape the enrichment can resolve to an uploads playlist. Returns null on junk. */
export function parseChannelRef(input: string | null | undefined): ChannelRef | null {
  if (!input) return null
  const s = String(input).trim()
  if (/^UC[\w-]{22}$/.test(s)) return { id: s }
  if (/^@[\w.-]+$/.test(s)) return { handle: s.slice(1) }
  const byId = s.match(/youtube\.com\/channel\/(UC[\w-]{22})/i)
  if (byId) return { id: byId[1] }
  const byAt = s.match(/youtube\.com\/@([\w.-]+)/i)
  if (byAt) return { handle: byAt[1] }
  const byUser = s.match(/youtube\.com\/user\/([\w.-]+)/i)
  if (byUser) return { username: byUser[1] }
  const byCustom = s.match(/youtube\.com\/c\/([\w.-]+)/i)
  if (byCustom) return { handle: byCustom[1] } // custom url → best-effort as a handle
  // a bare token (no url, not a playlist id) → treat as a handle best-effort
  if (/^[\w.-]+$/.test(s) && !/^(PL|UU|LL|FL|OL)/.test(s)) return { handle: s }
  return null
}

/** A YouTube channel's uploads playlist id is its channel id with the `UC` prefix swapped to `UU`. */
export function uploadsPlaylistIdFromChannelId(channelId: string): string {
  return /^UC[\w-]{22}$/.test(channelId) ? 'UU' + channelId.slice(2) : channelId
}

/* ---------- validation + normalization at boundaries ---------- */

/**
 * Parse arbitrary input into a complete, canonical Channel. Applies schema
 * defaults, then derives playlistId from the authored `playlist` and cleans the
 * video id list. Throws (ZodError) on anything that can't be a channel.
 */
export function parseChannel(input: unknown, origin?: ChannelOrigin): Channel {
  const ch = ChannelSchema.parse(input)
  ch.playlistId = ch.playlistId ?? parsePlaylist(ch.playlist)
  ch.videos = ch.videos.map((v) => parseVideo(v)).filter((v): v is string => Boolean(v))
  if (origin) ch.origin = origin
  if (!ch.slug) ch.slug = 'ch-' + ch.number
  return ch
}

/** Parse + sort a list of channels by dial number. Skips invalid entries. */
export function parseChannels(input: unknown, origin?: ChannelOrigin): Channel[] {
  const arr = Array.isArray(input) ? input : []
  const out: Channel[] = []
  for (const raw of arr) {
    const r = ChannelSchema.safeParse(raw)
    if (r.success) out.push(parseChannel(raw, origin))
  }
  return out.sort((a, b) => a.number - b.number)
}

/** safeParse a payload (blob / API response). Returns null if unusable. */
export function parsePayload(input: unknown): ChannelsPayload | null {
  const r = ChannelsPayloadSchema.safeParse(input)
  return r.success ? r.data : null
}
